import { useEffect } from 'react';
import type { ReactElement } from 'react';
import type { FieldProps } from '@rjsf/utils';
import { Box, Checkbox, FormControlLabel, FormGroup, Typography } from '@mui/material';

// One quick-link tile of the Shortcuts section. `type` picks the tile's icon and
// destination on the website; `title` and `description` are the copy on it.
export interface ShortcutItem {
  type: string;
  title: string;
  description: string;
}

// Lets a pharmacy choose which of the section's quick-link tiles to show. The tiles on
// offer are the schema `default` array — the four the section type ships with — and the
// ones present in formData are the tiles currently shown. Ticking a tile adds it back in
// its default position with its default copy; unticking removes it. The last shown tile
// cannot be unticked: an empty list would leave the section rendering an empty state on
// the live website, and a pharmacy that wants no tiles at all removes the section instead.
// A list that arrives empty (the schema has no minItems, so the API accepts one) is put
// back to the defaults in the form state, so saving any other change cannot keep it empty.
// Use in uiSchema as `"ui:field": "shortcutsPicker"`.
export function ShortcutsPickerField({
  formData,
  schema,
  onChange,
  fieldPathId,
  disabled,
  readonly,
}: FieldProps<ShortcutItem[]>): ReactElement {
  const available = Array.isArray(schema.default)
    ? (schema.default as unknown as ShortcutItem[])
    : [];
  const isEmpty = !Array.isArray(formData) || formData.length === 0;
  const shown = isEmpty ? available : formData;

  useEffect(() => {
    if (isEmpty && available.length > 0) onChange(available, fieldPathId.path);
    // onChange is stable in RJSF; the list turning up empty is the sole trigger for this reset.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEmpty]);
  // A stored tile the schema no longer offers still renders on the website, so it stays
  // listed here instead of vanishing from the form.
  const options = [
    ...available,
    ...shown.filter((item) => !available.some((option) => option.type === item.type)),
  ];
  const isShown = (type: string) => shown.some((item) => item.type === type);
  const onlyOneShown = shown.length === 1;

  function toggle(option: ShortcutItem, checked: boolean) {
    const next = checked
      ? options
          .filter((candidate) => candidate.type === option.type || isShown(candidate.type))
          .map((candidate) => shown.find((item) => item.type === candidate.type) ?? candidate)
      : shown.filter((item) => item.type !== option.type);
    onChange(next, fieldPathId.path);
  }

  return (
    <Box className="vtwo:flex vtwo:flex-col vtwo:gap-2">
      <Box>
        <Typography variant="body2" className="vtwo:font-medium vtwo:text-gray-700">
          Shortcuts to show
        </Typography>
        <Typography variant="body2" className="vtwo:text-gray-500">
          Untick a shortcut to hide it from the page. At least one has to stay on.
        </Typography>
      </Box>
      <FormGroup>
        {options.map((option) => {
          const checked = isShown(option.type);
          return (
            <FormControlLabel
              key={option.type}
              className="vtwo:items-start vtwo:m-0"
              control={
                <Checkbox
                  color="featured"
                  checked={checked}
                  disabled={disabled || readonly || (checked && onlyOneShown)}
                  onChange={(event) => toggle(option, event.target.checked)}
                />
              }
              label={
                <Box className="vtwo:flex vtwo:flex-col vtwo:pt-[9px]">
                  <Typography variant="body2" className="vtwo:font-medium vtwo:text-gray-700">
                    {option.title}
                  </Typography>
                  <Typography variant="body2" className="vtwo:text-gray-500">
                    {option.description}
                  </Typography>
                </Box>
              }
            />
          );
        })}
      </FormGroup>
    </Box>
  );
}
